// ============================================================
// TMR Portal Admin — how a clinic application reads on screen (28 §9)
// ============================================================
import type {
  CompanyApplicationListItem,
  CompanyApplicationMode,
  CompanyApplicationStatus,
} from './contract';

/** The queue's tabs, in the order the team works them. */
export const APPLICATION_STATUS_OPTIONS: { value: CompanyApplicationStatus; label: string }[] = [
  { value: 'Submitted', label: 'Recebidas' },
  { value: 'InReview', label: 'Em análise' },
  { value: 'Approved', label: 'Aprovadas' },
  { value: 'Rejected', label: 'Reprovadas' },
];

/**
 * The `StatusBadge` key of each status. Prefixed because `Approved`/`Rejected` already belong to the access
 * requests, with a label of their own; the keys are registered in `STATUS_MAP`.
 */
export const APPLICATION_STATUS_BADGE: Record<CompanyApplicationStatus, string> = {
  Submitted: 'solicitacao_recebida',
  InReview: 'solicitacao_em_analise',
  Approved: 'solicitacao_aprovada',
  Rejected: 'solicitacao_reprovada',
};

/** @param status A status from the API. */
export function applicationStatusLabel(status: string): string {
  return APPLICATION_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

const MODE_LABEL: Record<CompanyApplicationMode, string> = {
  Quick: 'Rápido',
  Complete: 'Completo',
};

/** @param mode `Quick` or `Complete`. */
export function applicationModeLabel(mode: string): string {
  return MODE_LABEL[mode as CompanyApplicationMode] ?? mode;
}

/** The site's catalogue of specialties, by key. */
const SPECIALTY_LABEL: Record<string, string> = {
  AestheticsAndBeauty: 'Estética e Beleza',
  AestheticAndIntegrativeMedicine: 'Medicina Estética e Integrativa',
  Dentistry: 'Odontologia',
  MentalHealth: 'Saúde Mental',
  Veterinary: 'Veterinária',
  Other: 'Outra',
};

/**
 * A specialty as the operator reads it. A key the site added before this screen keeps its own name rather
 * than disappearing.
 *
 * @param specialty The catalogue key.
 */
export function specialtyLabel(specialty: string): string {
  return SPECIALTY_LABEL[specialty] ?? specialty;
}

/** The monthly revenue bands the clinic picks from — what it declares, not the Empresa's classification. */
const REVENUE_BAND_LABEL: Record<string, string> = {
  UpTo30k: 'Até R$ 30 mil por mês',
  From30kTo100k: 'De R$ 30 mil a R$ 100 mil por mês',
  From100kTo300k: 'De R$ 100 mil a R$ 300 mil por mês',
  Above300k: 'Acima de R$ 300 mil por mês',
};

/** @param band The catalogue key. */
export function revenueBandLabel(band: string): string {
  return REVENUE_BAND_LABEL[band] ?? band;
}

/** @param type `Checking` or `Savings`. */
export function bankAccountTypeLabel(type: string): string {
  return type === 'Savings' ? 'Poupança' : type === 'Checking' ? 'Conta corrente' : type;
}

/**
 * What the duplicate mark says, or null when there is none. The clinic never sees it — the site answers
 * the same way either way — so this is the only place it is ever read.
 *
 * @param application The application's two duplicate references.
 */
export function duplicateLabel(
  application: Pick<CompanyApplicationListItem, 'duplicateOfCompanyId' | 'duplicateOfApplicationId'>,
): string | null {
  if (application.duplicateOfCompanyId) return 'CNPJ já é cliente';
  if (application.duplicateOfApplicationId) return 'Outra solicitação aberta';
  return null;
}

/** Whether the team can still act on it. */
export function isOpen(status: string): boolean {
  return status === 'Submitted' || status === 'InReview';
}

/**
 * A moment as `06/10/2026 14:30`, in the operator's clock.
 *
 * @param iso The ISO 8601 instant from the API.
 */
export function formatMoment(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
